/* eslint-disable no-lonely-if */
/**
 * Copyright (c) Nicolas Gallagher.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

/**
 * WARNING: changes to this file in particular can cause significant changes to
 * the results of render performance benchmarks.
 */
import { canUseDOM } from "fbjs/lib/ExecutionEnvironment"
import createCSSStyleSheet from "./createCSSStyleSheet"
import createCompileableStyle from "./createCompileableStyle"
import createOrderedCSSStyleSheet, {
  OrderedCSSStyleSheetType,
} from "./createOrderedCSSStyleSheet"
import flattenArray from "./modules/flattenArray"
import flattenStyle from "./flattenStyle"
import I18nManager from "./I18nManager"
import i18nStyle from "./i18nStyle"
import { atomic, classic, inline, stringifyValueWithProperty } from "./compile"
import { Style, StyleWrapper, Value } from "./types"
import initialRules from "./initialRules"
import modality from "./modality"
import { STYLE_ELEMENT_ID, STYLE_GROUPS } from "./constants"
import ReactNativePropRegistry from "./ReactNativePropRegistry"

export type ResolvedType = {
  css: {
    [key: string]: {
      rules: string[]
      group: number
    }
  }
  ltr: object
  rtl: object
  rtlNoSwap: object
}

export type InsertedType = {
  css: {
    [key: string]: boolean
  }
  ltr: object
  rtl: object
  rtlNoSwap: object
}

type PropsType = {
  className?: string
  classList?: string[]
  style?: Style
}

function createSheet(initSheet?: CSSStyleSheet) {
  return createOrderedCSSStyleSheet(
    initSheet || createCSSStyleSheet(STYLE_ELEMENT_ID),
  )
}

export default function createStyleResolver() {
  let inserted: InsertedType
  let cache: object
  let sheet: OrderedCSSStyleSheetType | undefined
  const resolved: ResolvedType = { css: {}, ltr: {}, rtl: {}, rtlNoSwap: {} }

  const init = (initSheet?: CSSStyleSheet) => {
    inserted = { css: {}, ltr: {}, rtl: {}, rtlNoSwap: {} }
    sheet = createSheet(initSheet)
    cache = {}
    modality((rule: string) => sheet?.insert(rule, STYLE_GROUPS.modality))
    initialRules.forEach((rule) => {
      sheet?.insert(rule, STYLE_GROUPS.reset)
    })
  }

  init()

  function addToCache(
    className: string,
    prop: string | number,
    value: string | number,
  ) {
    if (!cache[prop]) {
      cache[prop] = {}
    }
    cache[prop][value] = className
  }

  function getClassName(prop: string | number, value: string | number) {
    const val = stringifyValueWithProperty(value, prop)
    return cache[prop] && cache[prop].hasOwnProperty(val) && cache[prop][val]
  }

  function _injectRegisteredStyle(wrapper: StyleWrapper<Object>) {
    const id = wrapper.__styleId
    const { doLeftAndRightSwapInRTL, isRTL } = I18nManager
    let dir = "ltr"
    if (isRTL) {
      dir = doLeftAndRightSwapInRTL ? "rtl" : "rtlNoSwap"
    }
    if (!inserted[dir][id]) {
      const style = createCompileableStyle(
        i18nStyle(flattenStyle(wrapper) as Style),
      )
      const results = atomic(style)
      Object.keys(results).forEach((key) => {
        const { identifier, property, rules, value } = results[key]
        if (property) {
          if (value) {
            addToCache(identifier, property, value)
          }
          rules.forEach((rule) => {
            const group = STYLE_GROUPS.custom[property] || STYLE_GROUPS.atomic
            sheet?.insert(rule, group)
          })
        }
      })
      inserted[dir][id] = true
    }
  }

  /**
   * Resolves a React Native style object to DOM attributes
   */
  function resolve(style: Value, classList?: string[]) {
    const nextClassList = []
    let props: PropsType = {}

    if (!style && !classList) {
      return props
    }

    if (Array.isArray(classList)) {
      flattenArray(classList).forEach((identifier) => {
        if (identifier) {
          if (
            inserted.css[identifier] == null &&
            resolved.css[identifier] != null
          ) {
            const item = resolved.css[identifier]
            item.rules.forEach((rule) => {
              sheet?.insert(rule, item.group)
            })
            inserted.css[identifier] = true
          }

          nextClassList.push(identifier)
        }
      })
    }

    if (ReactNativePropRegistry.isStyleWrapper(style)) {
      // fast and cachable
      _injectRegisteredStyle(style as StyleWrapper<typeof style>)
      const key = createCacheKey(style["__styleId"])
      props = _resolveStyle(style, key)
    } else if (!Array.isArray(style)) {
      // resolve a plain RN style object
      props = _resolveStyle(style)
    } else {
      // flatten the style array
      // cache resolved props when all styles are registered
      // otherwise fallback to resolving
      const flatArray = flattenArray(style)
      let isArrayOfStyleWrappers = true
      let cacheKey = ""
      for (let i = 0; i < flatArray.length; i++) {
        const style = flatArray[i]
        if (ReactNativePropRegistry.isStyleWrapper(style)) {
          const wrapper = style as StyleWrapper<typeof style>
          const id = wrapper.__styleId
          if (isArrayOfStyleWrappers) {
            cacheKey += `${id}-`
          }
          _injectRegisteredStyle(wrapper)
        } else {
          isArrayOfStyleWrappers = false
        }
      }
      const key = isArrayOfStyleWrappers ? createCacheKey(cacheKey) : null
      props = _resolveStyle(flatArray, key)
    }

    if (props.classList) {
      nextClassList.push(...props.classList)
    }

    const finalProps: PropsType = {
      className: classListToString(nextClassList),
      classList: nextClassList,
    }
    if (props.style) {
      finalProps.style = props.style
    }

    return finalProps
  }

  /**
   * Resolves a React Native style object
   */
  function _resolveStyle(style: Value, key?: string | null) {
    const { doLeftAndRightSwapInRTL, isRTL } = I18nManager
    let dir = "ltr"
    if (isRTL) {
      dir = doLeftAndRightSwapInRTL ? "rtl" : "rtlNoSwap"
    }

    // faster: memoized
    if (key != null && resolved[dir][key] != null) {
      return resolved[dir][key]
    }

    const flatStyle = flattenStyle(style)!
    const localizedStyle = createCompileableStyle(i18nStyle(flatStyle))

    const initVal: PropsType = { classList: [] }
    // slower: convert style object to props and cache
    const props = Object.keys(localizedStyle)
      .sort()
      .reduce((props, styleProp) => {
        const value = localizedStyle[styleProp]
        if (value != null) {
          const className = getClassName(styleProp, value as string)
          if (className) {
            props.classList?.push(className)
          } else {
            // Certain properties and values are not transformed by 'createReactDOMStyle' as they
            // require more complex transforms into multiple CSS rules. Here we assume that StyleManager
            // can bind these styles to a className, and prevent them becoming invalid inline-styles.
            if (
              styleProp === "animationKeyframes" ||
              styleProp === "placeholderTextColor" ||
              styleProp === "pointerEvents" ||
              styleProp === "scrollbarWidth"
            ) {
              const a = atomic({ [styleProp]: value })
              Object.keys(a).forEach((key) => {
                const { identifier, rules } = a[key]
                props.classList?.push(identifier)
                rules.forEach((rule) => {
                  sheet?.insert(rule, STYLE_GROUPS.atomic)
                })
              })
            } else {
              if (!props.style) {
                props.style = {}
              }
              // 4x slower render
              props.style[styleProp] = value
            }
          }
        }
        return props
      }, initVal)

    if (props.style) {
      props.style = inline(props.style)
    }

    if (key != null) {
      resolved[dir][key] = props
    }

    return props
  }

  return {
    initStyleSheet(initSheet?: CSSStyleSheet) {
      init(initSheet)
    },
    getStyleSheet() {
      const textContent = sheet?.getTextContent()
      // Reset state on the server so critical css is always the result
      if (!canUseDOM) {
        init()
      }

      return {
        id: STYLE_ELEMENT_ID,
        textContent,
      }
    },
    createCSS(rules: { [key: string]: Style }, group?: number) {
      const result: { [key in keyof typeof rules]: string } = {}
      Object.keys(rules).forEach((name) => {
        const style = rules[name]
        const compiled = classic(style, name)

        Object.keys(compiled).forEach((key) => {
          const { identifier, rules } = compiled[key]
          resolved.css[identifier] = {
            group: group || STYLE_GROUPS.classic,
            rules,
          }
          result[name] = identifier
        })
      })
      return result
    },
    resolve,
    sheet,
  }
}

/**
 * Misc helpers
 */
const createCacheKey = (id: string | number) => {
  const prefix = "rn"
  return `${prefix}-${id}`
}

const classListToString = (list: string[]) => list.join(" ").trim()
